import { css } from '@emotion/react';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import { FormLoadingButton } from '../../../../../components/Form/FormComponents';
import { CompanyLogoAndName } from '../../../../../components/grid-renderers/CompanyCellRenderer';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import {
  IKPIRequestDataModel,
  IKPIRequestResponse,
  KPIRequestStatus,
} from '../../../../../data-models/kpi-requests.data-model';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { FMT, FormatterService } from '../../../../../util/formatter-service';
import { useEnableManagedServices } from './managed-services-utils';

const headerContainerCSS = css`
  display: grid;
  align-items: center;
  grid-template-columns: max-content max-content 1fr max-content;
  grid-column-gap: 1rem;
`;

export interface IKPIReviewHeaderProps extends Pick<IKPIRequestResponse, 'submittedBy' | 'respondedAt'> {
  company: ICompanyDataModel;
  isModified: boolean;
  isSubmitting: boolean;
  kpiRequest: IKPIRequestDataModel;
  onAcceptResponse: () => void;
  onDeleteRequest: () => void | Promise<void>;
  onMoveBackToReview: () => void | Promise<void>;
  onRejectResponse: () => void;
}

export function KPIReviewHeader(props: IKPIReviewHeaderProps) {
  const {
    company,
    isModified,
    isSubmitting,
    kpiRequest,
    onDeleteRequest,
    onAcceptResponse,
    onRejectResponse,
    onMoveBackToReview,
    respondedAt,
    submittedBy,
  } = props;

  return (
    <div css={headerContainerCSS}>
      <RequestDetails
        companyName={company.name}
        companyLogoUrl={company.logoUrl ?? ''}
        submittedBy={submittedBy}
        respondedAt={respondedAt}
      />
      <div />
      <KPIReviewButtons
        isModified={isModified}
        isSubmitting={isSubmitting}
        onAcceptResponse={onAcceptResponse}
        onRejectResponse={onRejectResponse}
        kpiRequest={kpiRequest}
        onMoveBackToReview={onMoveBackToReview}
        onDeleteRequest={onDeleteRequest}
      />
    </div>
  );
}

interface IKPIRecipientViewFooterProps {
  isModified: boolean;
  isSubmitting: boolean;
  onRejectResponse: () => void;
  onAcceptResponse: () => void;
  onMoveBackToReview: () => void;
  kpiRequest: IKPIRequestDataModel;
  onDeleteRequest: () => void;
}

export function KPIReviewButtons({
  isModified,
  isSubmitting,
  onDeleteRequest,
  onRejectResponse,
  onAcceptResponse,
  kpiRequest,
  onMoveBackToReview,
}: IKPIRecipientViewFooterProps) {
  const enableManagedServices = useEnableManagedServices();

  if (
    PermissionService.get().hasPermission(PermissionKey.canWriteDataCollection) &&
    kpiRequest.status === KPIRequestStatus.Accepted
  ) {
    return <MoveBackToReview onMoveBackToReview={onMoveBackToReview} isSubmitting={isSubmitting} />;
  }

  if (
    PermissionService.get().hasPermission(PermissionKey.canWriteDataCollection) &&
    kpiRequest.status === KPIRequestStatus.Rejected
  ) {
    return (
      <Stack direction='row' gap='1rem' height={'min-content'}>
        <Button onClick={onDeleteRequest} size='medium' variant='outlined' color='error'>
          Delete Request
        </Button>
        <MoveBackToReview onMoveBackToReview={onMoveBackToReview} isSubmitting={isSubmitting} />
      </Stack>
    );
  }

  return (
    <Stack direction='row' gap='1rem' height={'min-content'}>
      <LoadingButton
        loading={isSubmitting}
        onClick={onRejectResponse}
        size='medium'
        variant='outlined'
        color='error'
      >
        Reject
      </LoadingButton>
      <LoadingButton
        loading={isSubmitting}
        onClick={onAcceptResponse}
        size='medium'
        variant='contained'
        color='secondary'
      >
        {isModified ? 'Save changes & Accept' : 'Accept'}
      </LoadingButton>
      {enableManagedServices && kpiRequest.status == KPIRequestStatus.ManagedServiceFinished && (
        <MoveBackToReview onMoveBackToReview={onMoveBackToReview} isSubmitting={isSubmitting} />
      )}
    </Stack>
  );
}

export function MoveBackToReview({
  onMoveBackToReview,
  isSubmitting,
}: {
  onMoveBackToReview: () => void | Promise<void>;
  isSubmitting: boolean;
}) {
  return (
    <FormLoadingButton loading={isSubmitting} onClick={onMoveBackToReview} size='medium'>
      Move Back to Review
    </FormLoadingButton>
  );
}

interface IRequestDetailsProps extends Pick<IKPIRequestResponse, 'submittedBy' | 'respondedAt'> {
  companyName: string;
  companyLogoUrl?: string;
}
export function RequestDetails({
  companyName,
  companyLogoUrl,
  respondedAt,
  submittedBy,
}: IRequestDetailsProps) {
  const { colors } = useTheme();
  const respondentFormatter = FormatterService.get().getFormatterForId('userByEmail');

  return (
    <>
      <CompanyLogoAndName
        name={companyName}
        logoUrl={companyLogoUrl ?? ''}
        typographyProps={{ fontSize: '26px' }}
        avatarProps={{ size: 'large' }}
      />
      <Stack direction='column' justifyContent={'center'}>
        <Typography variant='body2' sx={{ color: colors.gray[700] }}>
          <b>Submitted by:</b> {respondentFormatter(submittedBy)}
        </Typography>
        <Typography variant='body2' sx={{ color: colors.gray[700] }}>
          <b>On:</b> {FMT.format('date', respondedAt) || '-'}
        </Typography>
      </Stack>
    </>
  );
}
